import * as React from "react";

import * as graphics from "@client/app/graphics";
import * as router from "@client/router";

import {
    Label,
    PFormHandler,
    PWidget,
    submitForm,
    useAutoPform,
} from "@client/site/forms";
import {server} from "@reactivated";

type ContactFormProps = {
    label: string;
    form: PFormHandler<server.core.rpc.Contact>;
    isEmailDisabled: boolean;
};

const ContactForm = (props: ContactFormProps) => {
    return (
        <div style={{display: "flex", flexDirection: "column", gap: 25}}>
            <Label
                label={props.form.fields.email.label}
                error={props.form.errors?.email?.[0] ?? null}
            >
                <PWidget
                    field={{
                        ...props.form.fields.email,
                        readOnly: props.isEmailDisabled,
                    }}
                />
            </Label>
            <Label label={props.label} error={props.form.errors?.message?.[0] ?? null}>
                <PWidget field={props.form.fields.message} />
            </Label>
        </div>
    );
};

export const Contact = router.Contact.withHooks(({profile}) => {
    const form = useAutoPform(
        server.core.rpc.Contact,
        profile == null
            ? undefined
            : {
                  email: profile.user.email,
                  message: "",
                  type: "SUPPORT",
              },
    );

    return {
        form,
        profile,
        manager_name: "",
    };
})
    .withTab({name: "support", title: "Support", icon: "0117-wrench"}, (props) => (
        <ContactForm
            form={props.form}
            label="Technical issues or questions about the app"
            isEmailDisabled={props.profile != null}
        />
    ))
    .withTab(
        {
            name: "manager",
            title: "My Manager",
            icon: "0287-user",
            guard: (props) => {
                const manager_name = props.profile?.user.manager_name;

                if (manager_name == null) {
                    return false;
                }

                return {
                    ...props,
                    manager_name,
                };
            },
        },
        (props) => (
            <ContactForm
                form={props.form}
                label={`Message to your manager — ${props.manager_name}`}
                isEmailDisabled={props.profile != null}
            />
        ),
    )
    .withTab({name: "feedback", title: "Feedback", icon: "0687-bullhorn"}, (props) => (
        <ContactForm
            form={props.form}
            label="Tell us what you like about Joy or how we can improve"
            isEmailDisabled={props.profile != null}
        />
    ))
    .withActions((props) => [
        {
            icon: "send",
            title: "Submit",
            onClick: async () => {
                const {type, message} =
                    props.currentTab == "feedback"
                        ? {
                              type: "FEEDBACK" as const,
                              message:
                                  "Thank you for your feedback. \n\nWe'll get back to you with our thoughts soon. \n\nCheck your spam folder in case our reply ends up there.",
                          }
                        : props.currentTab == "manager"
                          ? {
                                type: "MANAGER" as const,
                                message: `Your message was sent to ${props.manager_name}.`,
                            }
                          : {
                                type: "SUPPORT" as const,
                                message:
                                    "Your message was sent to our support team. \n\nWe'll get back to you within a day. \n\nCheck your spam folder in case our reply ends up there.",
                            };

                const result = await submitForm(
                    server.core.rpc.contact,
                    {...props.form.values, type},
                    props.form,
                );

                if (result.type === "success") {
                    props.form.reset();
                    props.notify(message);
                }
            },
        },
    ])
    .withOptions((props) => ({
        title: "Contact",
        titleSection:
            props.profile != null ? undefined : (
                <div>
                    <graphics.DarkLogoWide className="h-auto w-56" />
                </div>
            ),
    }));
